/**
 * TMS Jobs List Page — Container Trucking Jobs
 * Based on _spec_trucking_a1_v1.md and container-trucking skill
 *
 * Job types: IMP, IMP+RET, EXP, COL+EXP, T/T, REPO, STR
 * Data: fetched from entity API (slug: jobs)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRenderMode } from '@/hooks/useRenderMode';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Truck, Search, Plus, Filter, Download, RefreshCcw,
  AlertTriangle, ChevronRight, Container, Clock, User, MapPin, Loader2,
  Ship, Calendar, Package, Pencil,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useMomentumPageStore } from '@/stores/momentum-page-store';
import {
  DESKTOP_STANDARD_SHEET_WIDTH_CLASS, Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '@/components/ui/sheet';

// ── Types ────────────────────────────────────────────────────────────────────

type JobStatus =
  | 'draft' | 'booked' | 'confirmed' | 'collecting' | 'at_port'
  | 'in_transit' | 'delivered' | 'empty_return' | 'completed' | 'cancelled';

type JobType = 'IMP' | 'IMP+RET' | 'EXP' | 'COL+EXP' | 'T/T' | 'REPO' | 'STR';

interface TruckingJob {
  id: string;
  jobNumber: string;
  type: JobType;
  serviceMode: string;
  container_no: string;
  container_size: '20GP' | '40GP' | '40HC' | '45HC';
  customer: string;
  customer_ref: string;
  do_no: string;
  port: string;
  delivery_address: string;
  pickup_date: string;
  delivery_date: string;
  lfd: string;
  status: JobStatus;
  driver_name: string | null;
  driver_phone: string | null;
  bl_no: string;
  seal_no: string;
  urgent: boolean;
  agent_flags: string[];
  cargo_desc: string;
  created_at: string;
  // v2.4 transport fields
  mode: string;
  direction: string;
  origin: string;
  destination: string;
  commercial_status: string;
  payment_status: string;
  invoice_number: string;
  billing_group_status: string;
  outstanding_amount: number;
}

// ── Status / Type Configs ─────────────────────────────────────────────────────

const STATUS_COLORS: Record<JobStatus, string> = {
  draft:        'bg-gray-100 text-gray-600 border-gray-200',
  booked:       'bg-blue-100 text-blue-700 border-blue-200',
  confirmed:    'bg-sky-100 text-sky-700 border-sky-200',
  collecting:   'bg-purple-100 text-purple-700 border-purple-200',
  at_port:      'bg-orange-100 text-orange-700 border-orange-200',
  in_transit:   'bg-yellow-100 text-yellow-700 border-yellow-200',
  delivered:    'bg-green-100 text-green-700 border-green-200',
  empty_return: 'bg-teal-100 text-teal-700 border-teal-200',
  completed:    'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled:    'bg-red-100 text-red-600 border-red-200',
};

const TYPE_COLORS: Record<JobType, string> = {
  'IMP':     'bg-cyan-50 text-cyan-700',
  'IMP+RET': 'bg-blue-50 text-blue-700',
  'EXP':     'bg-indigo-50 text-indigo-700',
  'COL+EXP': 'bg-violet-50 text-violet-700',
  'T/T':     'bg-slate-50 text-slate-700',
  'REPO':    'bg-gray-50 text-gray-700',
  'STR':     'bg-teal-50 text-teal-700',
};

const SERVICE_MODE_LABELS: Record<string, { label: string; color: string }> = {
  ROUND_TRIP:   { label: 'Round Trip',   color: 'bg-blue-50 text-blue-700 border-blue-200' },
  ONE_WAY:      { label: 'One Way',      color: 'bg-slate-50 text-slate-600 border-slate-200' },
  STREET_TURN:  { label: 'Street Turn',  color: 'bg-teal-50 text-teal-700 border-teal-200' },
};

const FLAG_ICONS: Record<string, { label: string; color: string }> = {
  'tracker:updated':     { label: '📡 Tracked', color: 'text-blue-600' },
  'tracker:hold_found':  { label: '🚫 HOLD', color: 'text-red-600' },
  'watch:lfd_alert':     { label: '⏰ LFD Alert', color: 'text-orange-600' },
  'jess:created':        { label: '🤖 AI Created', color: 'text-purple-600' },
  'helen:invoice_ready': { label: '💰 Invoice Ready', color: 'text-green-600' },
  'jerry:street_turn_match': { label: '🔄 Street Turn', color: 'text-teal-600' },
  'grace:notified':      { label: '📤 Notified', color: 'text-sky-600' },
  'bean:invoiced':       { label: '✅ Invoiced', color: 'text-emerald-600' },
};

const COMMERCIAL_COLORS: Record<string, string> = {
  delivery_in_progress: 'bg-slate-100 text-slate-700 border-slate-200',
  proof_pending: 'bg-amber-100 text-amber-800 border-amber-200',
  ready_to_cost: 'bg-sky-100 text-sky-800 border-sky-200',
  cost_review: 'bg-violet-100 text-violet-800 border-violet-200',
  ready_to_invoice: 'bg-blue-100 text-blue-800 border-blue-200',
  invoiced: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  partial_paid: 'bg-orange-100 text-orange-800 border-orange-200',
  settled: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  disputed: 'bg-rose-100 text-rose-800 border-rose-200',
};

function labelize(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function money(amount: number, currency = 'SGD') {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

const JOB_LIST_TIMEOUT_MS = 5_000;

async function fetchJobsPage(): Promise<{ items: Record<string, unknown>[]; total: number }> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), JOB_LIST_TIMEOUT_MS);
  try {
    return await apiFetch<{ items: Record<string, unknown>[]; total: number }>(
      '/jobs?page=1&limit=100&includeTotal=false',
      { signal: controller.signal },
    );
  } finally {
    window.clearTimeout(timeout);
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TmsJobsPage() {
  const isMobile = useRenderMode() === 'mobile';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [serviceModeFilter, setServiceModeFilter] = useState<string>('all');
  const [allJobs, setAllJobs] = useState<TruckingJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [viewJob, setViewJob] = useState<TruckingJob | null>(null);
  const PAGE_SIZE = 25;

  // ── Push data to Momentum signal bar ──
  const setMomentumData = useMomentumPageStore((s) => s.setPageData);
  const clearMomentumData = useMomentumPageStore((s) => s.clearPageData);
  useEffect(() => {
    if (allJobs.length > 0) {
      setMomentumData({ pageKey: 'tms.jobs', entities: allJobs as unknown as Record<string, unknown>[] });
    }
    return () => { clearMomentumData(); };
  }, [allJobs, setMomentumData, clearMomentumData]);

  // Fetch from API, silent fallback to mock data
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJobsPage();
      const allItems = res?.items ?? [];

      setAllJobs(allItems.map((r: Record<string, unknown>): TruckingJob => {
        const d = (r.data ?? {}) as Record<string, unknown>;
        const jobType = String(r.jobType ?? r.job_type ?? '').toUpperCase();
        const status = String(r.status ?? 'draft').toLowerCase();
        return {
          id: String(r.id ?? ''),
          jobNumber: String(r.jobNumber ?? r.job_number ?? r.id ?? ''),
          type: (jobType || 'IMP') as JobType,
          serviceMode: String(r.serviceMode ?? r.service_mode ?? ''),
          container_no: String(r.containerNo ?? r.container_no ?? d.container_no ?? ''),
          container_size: String(r.containerSize ?? d.container_size ?? '40HC') as TruckingJob['container_size'],
          customer: String(r.customerName ?? r.customer_name ?? d.customer_name ?? d.client_name ?? d.client_code ?? r.customerId ?? r.customer_id ?? ''),
          customer_ref: String(d.customer_ref ?? d.client_ref ?? ''),
          do_no: String(d.do_no ?? ''),
          port: String(d.terminal ?? d.port ?? ''),
          delivery_address: String(r.destination ?? r.deliveryAddress ?? d.delivery_to ?? ''),
          pickup_date: String(r.eta ?? d.pickup_date ?? '').slice(0, 10),
          delivery_date: String(d.delivery_date ?? ''),
          lfd: String(d.lfd ?? d.last_free_date ?? ''),
          status: status as JobStatus,
          driver_name: String(r.driver ?? d.driver_name ?? '') || null,
          driver_phone: String(d.driver_phone ?? '') || null,
          bl_no: String(d.bl_no ?? ''),
          seal_no: String(r.sealNo ?? d.seal_no ?? ''),
          urgent: Boolean(d.urgent ?? d.is_urgent ?? false),
          agent_flags: Array.isArray(d.agent_flags) ? d.agent_flags as string[] : [],
          cargo_desc: String(d.cargo_description ?? r.cargoDescription ?? ''),
          created_at: String(r.createdAt ?? r.created_at ?? ''),
          mode: String(r.businessType ?? r.business_type ?? r.mode ?? d.mode ?? ''),
          direction: String(r.direction ?? d.direction ?? ''),
          origin: String(r.origin ?? d.pol ?? ''),
          destination: String(r.destination ?? d.pod ?? ''),
          commercial_status: String(d.commercial_status ?? d.commercialStatus ?? ''),
          payment_status: String(d.payment_status ?? d.paymentStatus ?? ''),
          invoice_number: String(d.invoice_number ?? d.invoiceNumber ?? ''),
          billing_group_status: String(d.billing_group_status ?? d.billingGroupStatus ?? ''),
          outstanding_amount: Number(d.outstanding_amount ?? d.outstandingAmount ?? 0),
        };
      }));
    } catch {
      setAllJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Client-side filtering + search + pagination
  const filtered = useMemo(() => {
    let list = allJobs;
    if (statusFilter !== 'all') list = list.filter((j) => j.status === statusFilter);
    if (typeFilter !== 'all') list = list.filter((j) => j.type === typeFilter);
    if (serviceModeFilter !== 'all') list = list.filter((j) => j.serviceMode === serviceModeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((j) =>
        j.jobNumber.toLowerCase().includes(q) ||
        j.id.toLowerCase().includes(q) ||
        j.container_no.toLowerCase().includes(q) ||
        j.customer.toLowerCase().includes(q) ||
        j.do_no.toLowerCase().includes(q) ||
        j.bl_no.toLowerCase().includes(q) ||
        (j.driver_name ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allJobs, statusFilter, typeFilter, serviceModeFilter, search]);

  const effectiveTotal = filtered.length;
  const jobs = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasMore = page * PAGE_SIZE < effectiveTotal;

  // ── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Job ID, container, customer…" className="pl-9 h-10" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {/* Status filter chips */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {['all', 'booked', 'collecting', 'at_port', 'in_transit', 'delivered', 'completed'].map((s) => (
            <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
              className="h-7 text-xs shrink-0 capitalize" onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'All' : s.replace('_', ' ')}
            </Button>
          ))}
        </div>
        {/* Service mode filter chips */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {['all', 'ROUND_TRIP', 'ONE_WAY', 'STREET_TURN'].map((m) => (
            <Button key={m} size="sm" variant={serviceModeFilter === m ? 'default' : 'outline'}
              className="h-7 text-xs shrink-0" onClick={() => setServiceModeFilter(m)}>
              {m === 'all' ? 'All Modes' : SERVICE_MODE_LABELS[m]?.label ?? m}
            </Button>
          ))}
        </div>
        {/* Job cards */}
        {loading && <div className="text-center py-4 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</div>}
        <div className="space-y-2">
          {jobs.map((j) => {
            const sc = STATUS_COLORS[j.status] || '';
            const tc = TYPE_COLORS[j.type] || '';
            return (
              <div key={j.id} className="block cursor-pointer" onClick={() => setViewJob(j)}>
              <Card className={`overflow-hidden ${j.urgent ? 'border-red-300' : ''} hover:bg-muted/30 transition-colors`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-xs font-bold text-primary">{j.jobNumber}</span>
                        {j.urgent && <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">URGENT</Badge>}
                      </div>
                      <div className="font-mono text-sm font-semibold mt-0.5">{j.container_no} <span className="text-xs text-muted-foreground">{j.container_size}</span></div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">{j.customer}</div>
                    </div>
                    <div className="flex flex-col gap-1 items-end shrink-0">
                      {j.serviceMode && SERVICE_MODE_LABELS[j.serviceMode] ? (
                        <Badge className={`text-[9px] ${SERVICE_MODE_LABELS[j.serviceMode]!.color}`}>
                          {SERVICE_MODE_LABELS[j.serviceMode]!.label}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className={`text-[9px] ${tc}`}>{j.type}</Badge>
                      )}
                      <Badge variant="outline" className={`text-[9px] ${sc}`}>{j.status.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.port}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> LFD {j.lfd}</span>
                  </div>
                  {j.driver_name && (
                    <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" /> {j.driver_name}
                      {j.driver_phone && <span> · {j.driver_phone}</span>}
                    </div>
                  )}
                      {j.commercial_status && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          <Badge variant="outline" className={`text-[9px] ${COMMERCIAL_COLORS[j.commercial_status] ?? 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                            {labelize(j.commercial_status)}
                          </Badge>
                          {j.invoice_number ? (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              Invoice {j.invoice_number}
                            </Badge>
                          ) : null}
                        </div>
                      )}
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {j.agent_flags.map((f) => {
                      const fi = FLAG_ICONS[f];
                      if (!fi) return null;
                      return <span key={f} className={`text-[10px] ${fi.color}`}>{fi.label}</span>;
                    })}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" asChild onClick={(event) => event.stopPropagation()}>
                      <Link to={`/tm/jobs/${j.id}`}>Open Job</Link>
                    </Button>
                    <Button size="sm" className="h-7 text-[11px]" asChild onClick={(event) => event.stopPropagation()}>
                      <Link to={`/tm/jobs/${j.id}/edit`}>
                        <Pencil className="mr-1 h-3 w-3" />
                        Edit Job
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search jobs, containers, customers..."
            className="pl-8 h-8 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="collecting">Collecting</SelectItem>
            <SelectItem value="at_port">At Port</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="IMP">IMP</SelectItem>
            <SelectItem value="IMP+RET">IMP+RET</SelectItem>
            <SelectItem value="EXP">EXP</SelectItem>
            <SelectItem value="COL+EXP">COL+EXP</SelectItem>
            <SelectItem value="T/T">T/T</SelectItem>
            <SelectItem value="REPO">REPO</SelectItem>
            <SelectItem value="STR">STR</SelectItem>
          </SelectContent>
        </Select>

        <Select value={serviceModeFilter} onValueChange={setServiceModeFilter}>
          <SelectTrigger className="h-8 w-[130px] text-xs">
            <SelectValue placeholder="Service Mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            <SelectItem value="ROUND_TRIP">Round Trip</SelectItem>
            <SelectItem value="ONE_WAY">One Way</SelectItem>
            <SelectItem value="STREET_TURN">Street Turn</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" className="h-8">
          <Filter className="mr-1.5 h-3.5 w-3.5" />
          More Filters
        </Button>

        <div className="flex items-center gap-1.5 ml-auto">
          <Button variant="outline" size="sm" className="h-8">
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export
          </Button>
          <Button size="sm" className="h-8" asChild>
            <Link to="/tm/jobs/new">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              New Job
            </Link>
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <span className="font-semibold text-foreground">TM Jobs Desk</span>
          <span className="text-muted-foreground">{loading ? '—' : effectiveTotal} jobs in view</span>
          <span className="text-muted-foreground">{filtered.filter((j) => j.commercial_status).length} with commercial status</span>
          {jobs.filter((j) => j.urgent).length > 0 ? (
            <span className="text-red-600">{jobs.filter((j) => j.urgent).length} urgent in current page</span>
          ) : null}
          <span className="text-muted-foreground">Planner-style list formalization in progress</span>
        </div>
      </div>

      {/* ── Jobs Table ───────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30">
                <tr className="border-b bg-muted/40">
                  {[
                    { label: 'Job Core', span: 2 },
                    { label: 'Movement', span: 3 },
                    { label: 'Customer', span: 1 },
                    { label: 'Execution', span: 3 },
                    { label: 'Commercial', span: 2 },
                    { label: 'Actions', span: 2 },
                  ].map((group) => (
                    <th
                      key={group.label}
                      colSpan={group.span}
                      className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {['Job No', 'Type', 'Mode', 'Route', 'Container', 'Customer', 'Port / Delivery', 'Dates', 'Driver', 'Status', 'Commercial', 'AI Flags', 'Actions'].map((col) => (
                    <th key={col} className="text-left px-3 py-2.5 text-xs text-muted-foreground font-medium whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={13} className="text-center py-6 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</td></tr>
                ) : jobs.length === 0 ? (
                  <tr><td colSpan={13} className="text-center py-6 text-xs text-muted-foreground">No jobs found</td></tr>
                ) : jobs.map((job) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const isLfdSoon = job.lfd !== '' && job.lfd <= today;
                  return (
                    <tr key={job.id} className={`border-b last:border-0 hover:bg-muted/20 ${job.urgent ? 'bg-red-50/30' : ''}`}>
                      {/* Job No */}
                      <td className="px-3 py-3">
                        <button onClick={() => setViewJob(job)} className="font-mono text-xs font-medium text-primary hover:underline text-left">
                          {job.jobNumber}
                        </button>
                        <div className="text-[10px] text-muted-foreground mt-0.5">{job.do_no}</div>
                      </td>

                      {/* Service Mode (primary) + Trip Type (secondary) */}
                      <td className="px-3 py-3">
                        {job.serviceMode && SERVICE_MODE_LABELS[job.serviceMode] ? (
                          <>
                            <Badge className={`text-[10px] font-bold ${SERVICE_MODE_LABELS[job.serviceMode]!.color}`}>
                              {SERVICE_MODE_LABELS[job.serviceMode]!.label}
                            </Badge>
                            <div className="mt-1">
                              <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[9px] ${TYPE_COLORS[job.type]}`}>
                                {job.type}
                              </span>
                            </div>
                          </>
                        ) : (
                          <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold ${TYPE_COLORS[job.type]}`}>
                            {job.type}
                          </span>
                        )}
                      </td>

                      {/* Mode */}
                      <td className="px-3 py-3">
                        {job.mode && (
                          <div>
                            <Badge variant="outline" className="text-[10px] px-1.5 capitalize">{job.mode}</Badge>
                            <div className="text-[10px] text-muted-foreground mt-0.5 capitalize">{job.direction}</div>
                          </div>
                        )}
                      </td>

                      {/* Route */}
                      <td className="px-3 py-3">
                        {(job.origin || job.destination) && (
                          <div className="text-xs font-mono">
                            <span className="font-medium">{job.origin}</span>
                            {job.origin && job.destination && <span className="text-muted-foreground"> → </span>}
                            <span className="font-medium">{job.destination}</span>
                          </div>
                        )}
                      </td>

                      {/* Container */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <Container className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div>
                            <div className="font-mono text-xs font-medium">{job.container_no}</div>
                            <div className="text-[10px] text-muted-foreground">{job.container_size}</div>
                          </div>
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="px-3 py-3">
                        <div className="text-xs font-medium truncate max-w-[140px]">{job.customer}</div>
                        <div className="text-[10px] text-muted-foreground">{job.customer_ref}</div>
                      </td>

                      {/* Port / Delivery */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="truncate max-w-[150px]">{job.port}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[150px] mt-0.5 ml-4">{job.cargo_desc}</div>
                      </td>

                      {/* Dates */}
                      <td className="px-3 py-3 font-mono text-[10px]">
                        <div className="text-muted-foreground">Pickup: {job.pickup_date}</div>
                        <div className="text-muted-foreground">Del: {job.delivery_date}</div>
                        <div className={`font-semibold ${isLfdSoon ? 'text-red-600' : 'text-muted-foreground'}`}>
                          LFD: {job.lfd}
                        </div>
                      </td>

                      {/* Driver */}
                      <td className="px-3 py-3">
                        {job.driver_name ? (
                          <div>
                            <div className="flex items-center gap-1 text-xs">
                              <User className="h-3 w-3 text-muted-foreground" />
                              {job.driver_name}
                            </div>
                            <div className="text-[10px] text-muted-foreground ml-4">{job.driver_phone}</div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">
                            Unassigned
                          </Badge>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 ${STATUS_COLORS[job.status]}`}
                        >
                          {job.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </Badge>
                        {job.urgent && (
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-red-600">
                            <AlertTriangle className="h-2.5 w-2.5" /> Urgent
                          </div>
                        )}
                      </td>

                      {/* Commercial */}
                      <td className="px-3 py-3">
                        {job.commercial_status ? (
                          <div className="space-y-1">
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 ${COMMERCIAL_COLORS[job.commercial_status] ?? 'bg-slate-100 text-slate-700 border-slate-200'}`}
                            >
                              {labelize(job.commercial_status)}
                            </Badge>
                            <div className="text-[10px] text-muted-foreground">
                              {job.invoice_number ? `Inv ${job.invoice_number}` : 'No invoice'}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {job.payment_status ? labelize(job.payment_status) : 'Payment pending'}
                            </div>
                            {job.outstanding_amount > 0 ? (
                              <div className="text-[10px] font-medium text-orange-700">{money(job.outstanding_amount)} outstanding</div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>

                      {/* AI Flags */}
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          {job.agent_flags.map((flag) => {
                            const flagCfg = FLAG_ICONS[flag];
                            return flagCfg ? (
                              <span key={flag} className={`text-[10px] ${flagCfg.color}`}>{flagCfg.label}</span>
                            ) : null;
                          })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]" asChild>
                            <Link to={`/tm/jobs/${job.id}`}>Open</Link>
                          </Button>
                          <Button size="sm" className="h-7 px-2 text-[10px]" asChild>
                            <Link to={`/tm/jobs/${job.id}/edit`}>
                              <Pencil className="mr-1 h-3 w-3" />
                              Edit
                            </Link>
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewJob(job)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
            <span>Showing {jobs.length} of {effectiveTotal} jobs · Page {page}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" className="h-7" disabled={!hasMore} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Quick View Panel ── */}
      <Sheet open={!!viewJob} onOpenChange={(o) => { if (!o) setViewJob(null); }}>
        <SheetContent className={`${DESKTOP_STANDARD_SHEET_WIDTH_CLASS} overflow-y-auto`}>
          {viewJob && (() => {
            const j = viewJob;
            const today = new Date().toISOString().slice(0, 10);
            const isLfdSoon = j.lfd && j.lfd <= today;
            return (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    {j.jobNumber}
                    <Badge className={`text-[10px] ml-1 ${TYPE_COLORS[j.type] ?? 'bg-gray-100'}`}>{j.type}</Badge>
                    <Badge className={`text-[10px] ${STATUS_COLORS[j.status] ?? 'bg-gray-100'}`}>{j.status.replace(/_/g, ' ')}</Badge>
                    {j.urgent && <Badge variant="destructive" className="text-[10px]">URGENT</Badge>}
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                  {/* Container & Mode */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-muted/40 rounded-lg p-3 space-y-2">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Container</div>
                      <div className="font-mono font-bold text-sm">{j.container_no || '—'}</div>
                      <div className="text-muted-foreground">{j.container_size} · Seal {j.seal_no || '—'}</div>
                    </div>
                    <div className="bg-muted/40 rounded-lg p-3 space-y-2">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Service Mode</div>
                      <div className="font-medium">{j.serviceMode && SERVICE_MODE_LABELS[j.serviceMode] ? SERVICE_MODE_LABELS[j.serviceMode]!.label : j.type}</div>
                      {j.mode && <div className="text-muted-foreground capitalize">{j.mode} · {j.direction}</div>}
                    </div>
                  </div>
                  {/* Customer */}
                  <div className="border rounded-lg p-3 space-y-2 text-xs">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Customer & References</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{j.customer || '—'}</span></div>
                      <div><span className="text-muted-foreground">Cust. Ref:</span> <span className="font-medium">{j.customer_ref || '—'}</span></div>
                      <div><span className="text-muted-foreground">B/L No:</span> <span className="font-mono">{j.bl_no || '—'}</span></div>
                      <div><span className="text-muted-foreground">D/O No:</span> <span className="font-mono">{j.do_no || '—'}</span></div>
                    </div>
                  </div>
                  {/* Dates */}
                  <div className="border rounded-lg p-3 space-y-2 text-xs">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Dates</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-2 bg-muted/30 rounded">
                        <div className="text-[10px] text-muted-foreground">Pickup</div>
                        <div className="font-medium">{j.pickup_date || '—'}</div>
                      </div>
                      <div className={`text-center p-2 rounded ${isLfdSoon ? 'bg-red-50 text-red-700' : 'bg-muted/30'}`}>
                        <div className="text-[10px] text-muted-foreground">LFD</div>
                        <div className="font-bold">{j.lfd || '—'}</div>
                      </div>
                      <div className="text-center p-2 bg-muted/30 rounded">
                        <div className="text-[10px] text-muted-foreground">Delivery</div>
                        <div className="font-medium">{j.delivery_date || '—'}</div>
                      </div>
                    </div>
                  </div>
                  {/* Route & Driver */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="border rounded-lg p-3 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Route</div>
                      <div className="font-medium">{j.origin || '—'} → {j.destination || '—'}</div>
                      <div className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{j.port || '—'}</div>
                    </div>
                    <div className="border rounded-lg p-3 space-y-1">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Driver</div>
                      <div className="font-medium flex items-center gap-1">{j.driver_name ? <><User className="h-3 w-3" />{j.driver_name}</> : '—'}</div>
                      <div className="text-muted-foreground">{j.driver_phone || ''}</div>
                    </div>
                  </div>
                  {/* Cargo */}
                  {j.cargo_desc && (
                    <div className="border rounded-lg p-3 text-xs">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">Cargo</div>
                      <div>{j.cargo_desc}</div>
                    </div>
                  )}
                  {/* AI Flags */}
                  {j.agent_flags.length > 0 && (
                    <div className="border rounded-lg p-3 text-xs">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">AI Flags</div>
                      <div className="flex flex-wrap gap-1">
                        {j.agent_flags.map((flag) => {
                          const fi = FLAG_ICONS[flag];
                          return fi ? <span key={flag} className={`text-[10px] ${fi.color}`}>{fi.label}</span> : null;
                        })}
                      </div>
                    </div>
                  )}
                  {/* Commercial */}
                  {j.commercial_status && (
                    <div className="border rounded-lg p-3 text-xs">
                      <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1">Commercial</div>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className={`text-[10px] ${COMMERCIAL_COLORS[j.commercial_status] ?? 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {labelize(j.commercial_status)}
                        </Badge>
                        {j.payment_status ? <Badge variant="outline" className="text-[10px]">{labelize(j.payment_status)}</Badge> : null}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                        <div><span className="text-muted-foreground">Invoice:</span> <span className="font-medium">{j.invoice_number || '—'}</span></div>
                        <div><span className="text-muted-foreground">Outstanding:</span> <span className="font-medium">{money(j.outstanding_amount)}</span></div>
                      </div>
                    </div>
                  )}
                </div>
                <SheetFooter className="mt-6">
                  <div className="flex w-full flex-wrap gap-2">
                    <Button size="sm" asChild>
                      <Link to={`/tm/jobs/${j.id}`}>Open Job</Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/tm/jobs/${j.id}/edit`}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Edit Job
                      </Link>
                    </Button>
                  </div>
                </SheetFooter>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
